import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  ErrorState,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  formatDate,
  formatDateTime,
} from '@lawbee/ui';
import { AlertTriangle, Bell, Check, Mail, Phone } from 'lucide-react';
import { useState } from 'react';
import { api, isApiError } from '../../../lib/api';
import { todayIn, useApiMutation, useParam } from '../../../lib/hooks';
import { useMe } from '../../../lib/me';
import type { CaseUpdate, MatterDetail, Stage } from '../../../lib/types';
import { DocumentRequestsTab, InvoicesTab, UpdatesTab } from './matter-comms';
import { DeadlinesTab, DocumentsTab, NotesTab, PartiesTab, TasksTab } from './matter-tabs';
import { DefinitionList, Panel, deadlineKindLabel, fullName, matterKey, useMatterInvalidate } from './shared';

export function MatterDetailPage() {
  const id = useParam('id');
  const q = useQuery({ queryKey: matterKey(id), queryFn: () => api.get<MatterDetail>(`/api/matters/${id}`), enabled: !!id });
  if (q.isLoading) return <LoadingState label="Loading matter" />;
  if (q.error || !q.data) {
    return (
      <div>
        <PageHeader title="Matter" breadcrumbs={<Link to="/matters">Matters</Link>} />
        <ErrorState title={isApiError(q.error, 'not_found') ? 'Matter not found' : 'Could not load this matter'} error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }
  return <MatterView data={q.data} />;
}

function MatterView({ data }: { data: MatterDetail }) {
  const m = data.matter;
  const invalidate = useMatterInvalidate(m.id);
  const [tab, setTab] = useState('overview');
  const [confirmClose, setConfirmClose] = useState(false);
  const setStatus = useApiMutation((status: 'open' | 'closed') => api.patch(`/api/matters/${m.id}`, { status }), {
    success: (_d) => (m.status === 'open' ? 'Matter closed' : 'Matter reopened'),
    onSuccess: () => {
      setConfirmClose(false);
      void invalidate();
    },
  });
  const client = m.client ? fullName(m.client.firstName, m.client.lastName) : null;
  const openTasks = data.tasks.filter((t) => t.status !== 'done').length;
  const openDeadlines = data.deadlines.filter((d) => d.status !== 'done').length;
  const pendingUpdates = data.updates.filter((u) => u.status === 'pending_approval').length;

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Link to="/matters" className="hover:text-foreground">
            Matters
          </Link>
        }
        title={m.title}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
            <span className="figures font-medium text-foreground">{m.number}</span>
            {m.status === 'open' ? <Badge variant="success">Open</Badge> : <Badge>Closed</Badge>}
            {client ? <span>{client}</span> : null}
            {m.type ? <span>{m.type.name}</span> : null}
          </span>
        }
        actions={
          m.status === 'open' ? (
            <Button variant="outline" onClick={() => setConfirmClose(true)}>
              Close matter
            </Button>
          ) : (
            <Button variant="outline" loading={setStatus.isPending} onClick={() => setStatus.mutate('open')}>
              Reopen matter
            </Button>
          )
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <TabsList aria-label="Matter sections">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="deadlines">
              Deadlines{openDeadlines ? <span className="figures text-muted-foreground">{openDeadlines}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="tasks">
              Tasks{openTasks ? <span className="figures text-muted-foreground">{openTasks}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="updates">
              Updates{pendingUpdates ? <span className="figures text-muted-foreground">{pendingUpdates}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="requests">Document requests</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="overview">
          <OverviewTab data={data} onTab={setTab} />
        </TabsContent>
        <TabsContent value="parties">
          <PartiesTab data={data} />
        </TabsContent>
        <TabsContent value="deadlines">
          <DeadlinesTab data={data} />
        </TabsContent>
        <TabsContent value="tasks">
          <TasksTab data={data} />
        </TabsContent>
        <TabsContent value="notes">
          <NotesTab data={data} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentsTab data={data} />
        </TabsContent>
        <TabsContent value="updates">
          <UpdatesTab data={data} />
        </TabsContent>
        <TabsContent value="requests">
          <DocumentRequestsTab data={data} />
        </TabsContent>
        <TabsContent value="invoices">
          <InvoicesTab data={data} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title="Close this matter?"
        description="Closed matters stop counting toward your open matter limit and drop out of check-ins. You can reopen it later."
        confirmLabel="Close matter"
        loading={setStatus.isPending}
        onConfirm={() => setStatus.mutate('closed')}
      />
    </div>
  );
}

function OverviewTab({ data, onTab }: { data: MatterDetail; onTab: (tab: string) => void }) {
  const me = useMe();
  const m = data.matter;
  const today = todayIn(me.workspace.timezone);
  const upcoming = data.deadlines.filter((d) => d.status !== 'done').slice(0, 4);
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-6">
        <StagePanel data={data} />
        <Panel title="Description">
          {m.description ? <p className="whitespace-pre-line text-sm leading-relaxed">{m.description}</p> : <p className="text-sm text-muted-foreground">No description.</p>}
        </Panel>
        {data.flags.length > 0 ? (
          <Panel title="Needs attention" description="Raised by automatic check-ins.">
            <ul className="space-y-2">
              {data.flags.map((f) => (
                <li key={f.id} className="flex items-start gap-2.5 text-sm">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                  <span className="min-w-0">
                    {f.message}
                    <span className="figures block text-[12px] text-muted-foreground">{formatDateTime(f.createdAt)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        ) : null}
      </div>
      <div className="min-w-0 space-y-6">
        <Panel title="Client">
          {m.client ? (
            <div className="space-y-2 text-sm">
              <p className="font-medium">{fullName(m.client.firstName, m.client.lastName)}</p>
              {m.client.email ? (
                <a href={`mailto:${m.client.email}`} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                  <Mail className="size-4" aria-hidden /> {m.client.email}
                </a>
              ) : (
                <p className="text-muted-foreground">No email on file. Updates cannot be emailed.</p>
              )}
              {m.client.phone ? (
                <a href={`tel:${m.client.phone}`} className="figures flex items-center gap-2 text-muted-foreground hover:text-foreground">
                  <Phone className="size-4" aria-hidden /> {m.client.phone}
                </a>
              ) : null}
              <div className="flex flex-wrap gap-2 pt-1">
                {m.client.portalEnabled ? <Badge variant="success">Portal enabled</Badge> : <Badge>No portal access</Badge>}
                {m.client.smsOptIn ? <Badge variant="info">SMS opt-in</Badge> : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No client on this matter.</p>
          )}
        </Panel>
        <Panel title="Details">
          <DefinitionList
            className="sm:grid-cols-[minmax(0,8rem)_1fr]"
            items={[
              { label: 'Responsible', value: m.responsibleName ?? 'Unassigned' },
              { label: 'Opened', value: <span className="figures">{formatDate(m.openedAt)}</span> },
              ...(m.closedAt ? [{ label: 'Closed', value: <span className="figures">{formatDate(m.closedAt)}</span> }] : []),
              { label: 'Last activity', value: <span className="figures">{formatDateTime(m.lastActivityAt)}</span> },
              ...(m.externalRef ? [{ label: 'External ref', value: <span className="figures">{m.externalRef}</span> }] : []),
            ]}
          />
        </Panel>
        <Panel
          title="Upcoming deadlines"
          actions={
            <Button size="sm" variant="link" onClick={() => onTab('deadlines')}>
              All deadlines
            </Button>
          }
        >
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
          ) : (
            <ul className="space-y-2.5">
              {upcoming.map((d) => (
                <li key={d.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{d.title}</span>
                    <span className="text-[12px] text-muted-foreground">{deadlineKindLabel(d.kind)}</span>
                  </span>
                  <span className={cn('figures shrink-0', d.dueOn < today && 'text-destructive')}>{formatDate(d.dueOn)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function StagePanel({ data }: { data: MatterDetail }) {
  const me = useMe();
  const m = data.matter;
  const invalidate = useMatterInvalidate(m.id);
  const stages = m.type?.stages ?? [];
  const currentIndex = stages.findIndex((s) => s.key === m.stageKey);
  const [target, setTarget] = useState<Stage | null>(null);
  const [lastUpdates, setLastUpdates] = useState<CaseUpdate[] | null>(null);
  const move = useApiMutation((stageKey: string) => api.post<{ ok: boolean; updates: CaseUpdate[] }>(`/api/matters/${m.id}/stage`, { stageKey }), {
    success: (res) =>
      res.updates.length === 0
        ? 'Stage updated'
        : res.updates.some((u) => u.status === 'sent')
          ? 'Stage updated. Client update sent.'
          : 'Stage updated. Client update drafted for approval.',
    onSuccess: (res) => {
      setLastUpdates(res.updates);
      setTarget(null);
      void invalidate();
    },
  });
  const modeText = me.settings.updateMode === 'auto' ? 'sent to the client automatically' : 'drafted for approval before it goes to the client';

  return (
    <Panel title="Stage" description={m.status === 'closed' ? 'Reopen the matter to change its stage.' : 'Click a stage to move the matter.'}>
      {stages.length === 0 ? (
        <p className="text-sm text-muted-foreground">This matter type has no stages.</p>
      ) : (
        <ol className="space-y-1" aria-label="Matter stages">
          {stages.map((s, i) => {
            const state = i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
            return (
              <li key={s.key}>
                <button
                  type="button"
                  disabled={m.status === 'closed' || state === 'current'}
                  aria-current={state === 'current' ? 'step' : undefined}
                  onClick={() => setTarget(s)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors enabled:hover:bg-foreground/[0.04] disabled:cursor-default',
                    state === 'current' && 'bg-accent',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold',
                      state === 'done' && 'bg-success text-white',
                      state === 'current' && 'bg-primary text-primary-foreground',
                      state === 'upcoming' && 'shadow-[inset_0_0_0_1.5px_var(--color-border)] text-muted-foreground',
                    )}
                    aria-hidden
                  >
                    {state === 'done' ? <Check className="size-3.5" /> : <span className="figures">{i + 1}</span>}
                  </span>
                  <span className={cn('min-w-0 flex-1', state === 'current' ? 'font-semibold' : state === 'upcoming' && 'text-muted-foreground')}>
                    {s.label}
                    <span className="sr-only">{state === 'done' ? ' (completed)' : state === 'current' ? ' (current stage)' : ' (upcoming)'}</span>
                  </span>
                  {s.notifyClient ? (
                    <Tooltip content="The client gets an update when the matter reaches this stage">
                      <span className="text-muted-foreground" aria-label="Client is notified at this stage">
                        <Bell className="size-3.5" aria-hidden />
                      </span>
                    </Tooltip>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {lastUpdates && lastUpdates.length > 0 ? (
        <Alert variant={lastUpdates.some((u) => u.status === 'sent') ? 'success' : 'info'} className="mt-4" title="Client updates from this stage change">
          <ul className="mt-1 space-y-1">
            {lastUpdates.map((u) => (
              <li key={u.id}>
                {u.status === 'sent' ? 'Update sent' : u.status === 'pending_approval' ? 'Update drafted for approval' : `Update ${u.status}`} ({u.channel === 'sms' ? 'SMS' : 'email'}){u.subject ? `: ${u.subject}` : ''}
              </li>
            ))}
          </ul>
          {lastUpdates.some((u) => u.status === 'pending_approval') ? (
            <Link to="/updates" className="mt-2 inline-block font-medium text-foreground underline-offset-4 hover:underline">
              Review in the approval queue
            </Link>
          ) : null}
        </Alert>
      ) : null}

      <ConfirmDialog
        open={!!target}
        onOpenChange={(o) => !o && setTarget(null)}
        title={`Move to ${target?.label ?? ''}?`}
        description={
          target?.notifyClient
            ? `A client update will be ${modeText}, per your update mode.`
            : 'This stage does not notify the client.'
        }
        confirmLabel="Move stage"
        loading={move.isPending}
        onConfirm={() => target && move.mutate(target.key)}
      />
    </Panel>
  );
}
